import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MicrophoneIcon,
  PauseCircleIcon,
  SpeakerWaveIcon,
} from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { GenerateTextCard } from "../../components/GenerateTextCard";
import useTranslation from "../../hooks/useTranslation";

export const Route = createFileRoute("/app/read_aloud_mockup")({
  component: ReadAloudMockupSection,
});

type SpeakState = "off" | "ready" | "recording" | "processing" | "feedback";

type SentenceMock = {
  id: number;
  text: string;
  translation: string;
  heard: string;
  hintKey:
    | "read_aloud_mockup_hint_missing_word"
    | "read_aloud_mockup_hint_slow_down"
    | "read_aloud_mockup_hint_word_order";
};

const SENTENCES: SentenceMock[] = [
  {
    id: 1,
    text: "I need a short break before I continue.",
    translation: "Ich brauche eine kurze Pause, bevor ich weitermache.",
    heard: "I need short break before I continue.",
    hintKey: "read_aloud_mockup_hint_missing_word",
  },
  {
    id: 2,
    text: "The small market near the station is still open.",
    translation: "Der kleine Markt beim Bahnhof ist noch offen.",
    heard: "The small market near the station is still open.",
    hintKey: "read_aloud_mockup_hint_slow_down",
  },
  {
    id: 3,
    text: "After dinner, we usually walk along the river.",
    translation:
      "Nach dem Abendessen gehen wir normalerweise am Fluss spazieren.",
    heard: "After dinner, we walk usually along the river.",
    hintKey: "read_aloud_mockup_hint_word_order",
  },
];

export function ReadAloudMockupSection() {
  const { translate } = useTranslation();
  const [speakState, setSpeakState] = useState<SpeakState>("off");
  const [activeSentenceIndex, setActiveSentenceIndex] = useState(0);
  const [, setAttempts] = useState(0);
  const timerRef = useRef<number | null>(null);
  const activeSentence = SENTENCES[activeSentenceIndex];

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const completedCount = useMemo(() => {
    if (speakState === "off") {
      return 0;
    }
    return activeSentenceIndex;
  }, [activeSentenceIndex, speakState]);

  const enterSpeakMode = () => {
    setSpeakState("ready");
    setActiveSentenceIndex(0);
    setAttempts(0);
  };

  const startRecording = () => {
    setSpeakState("recording");
  };

  const stopRecording = () => {
    setAttempts((prev) => prev + 1);
    setSpeakState("processing");
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setSpeakState("feedback");
      timerRef.current = null;
    }, 900);
  };

  const retrySentence = () => {
    setSpeakState("ready");
  };

  const moveToNextSentence = () => {
    if (activeSentenceIndex >= SENTENCES.length - 1) {
      setSpeakState("off");
      setActiveSentenceIndex(0);
      return;
    }
    setActiveSentenceIndex((prev) => prev + 1);
    setSpeakState("ready");
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6">
      <section className="rounded-[26px] border border-slate-200 bg-[linear-gradient(180deg,#f9fbfc_0%,#fdfefe_100%)] px-5 py-5 sm:px-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
          {translate("read_aloud_mockup_eyebrow")}
        </p>
        <h1 className="mt-3 font-serif text-3xl text-slate-900 sm:text-4xl">
          {translate("read_aloud_mockup_title")}
        </h1>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-700">
          {translate("read_aloud_mockup_subtitle")}
        </p>
      </section>

      <section className="mt-5 rounded-[26px] border border-slate-200 bg-white px-5 py-5 sm:px-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
            {translate("read_aloud_mockup_generator_label")}
          </p>
          <h2 className="mt-2 font-serif text-2xl text-slate-900">
            {translate("read_aloud_mockup_generator_title")}
          </h2>
          <p className="mt-2 max-w-2xl text-base leading-relaxed text-slate-700">
            {translate("read_aloud_mockup_generator_body")}
          </p>
        </div>
        <div className="rounded-[22px] border border-slate-200 bg-slate-50/60 p-3 sm:p-4">
          <GenerateTextCard />
        </div>
      </section>

      <section className="mt-5 rounded-[26px] border border-slate-200 bg-white px-5 py-5 sm:px-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
            {translate("read_aloud_mockup_details_label")}
          </p>
          <h2 className="mt-2 font-serif text-2xl text-slate-900">
            {translate("read_aloud_mockup_details_title")}
          </h2>
          <p className="mt-2 max-w-3xl text-base leading-relaxed text-slate-700">
            {translate("read_aloud_mockup_details_body")}
          </p>
        </div>

        <div className="bg-neutral-50 px-3 py-4 md:px-5">
          <div className="md:grid md:grid-cols-12 md:gap-11">
            <div className="relative mb-9 rounded-lg md:col-span-6 md:col-start-3 md:p-4">
              <div className="mb-2 mt-2 flex items-center justify-between text-slate-700">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  {translate("read_aloud_mockup_previous")}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700"
                >
                  {translate("read_aloud_mockup_next")}
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3 border-y border-slate-200 py-2">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="grid h-8 w-8 place-items-center rounded-full border border-slate-300 bg-white text-slate-700"
                  >
                    <PlayIcon className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                      {translate("read_aloud_mockup_audio_label")}
                    </p>
                    <p className="font-serif text-sm text-slate-700">
                      0:18 / 1:24
                    </p>
                  </div>
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] uppercase tracking-[0.14em] ${
                      speakState === "off"
                        ? "border-sky-700 bg-sky-50 text-sky-900"
                        : "border-slate-300 bg-white text-slate-700"
                    }`}
                    onClick={() => {
                      if (speakState === "off") {
                        enterSpeakMode();
                        return;
                      }
                      setSpeakState("off");
                    }}
                  >
                    <MicrophoneIcon className="h-4 w-4" />
                    {translate(
                      speakState === "off"
                        ? "read_aloud_mockup_enter_speak"
                        : "read_aloud_mockup_exit_speak",
                    )}
                  </button>
                </div>
              </div>

              {speakState !== "off" && (
                <div className="mt-4 rounded-[22px] border border-slate-200 bg-white px-4 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                        {translate("read_aloud_mockup_progress_label")}
                      </p>
                      <p className="mt-1 text-base font-semibold text-slate-900">
                        {translate("read_aloud_mockup_progress_value", {
                          current: String(activeSentenceIndex + 1),
                          total: String(SENTENCES.length),
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {SENTENCES.map((_, index) => (
                        <span
                          key={index}
                          className={`h-2.5 w-9 rounded-full ${
                            index < completedCount
                              ? "bg-emerald-500"
                              : index === activeSentenceIndex
                                ? "bg-sky-700"
                                : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-4">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      {translate("read_aloud_mockup_target_label")}
                    </p>
                    <p className="mt-2 text-xl leading-tight text-slate-950">
                      {activeSentence.text}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      {activeSentence.translation}
                    </p>
                  </div>

                  {speakState === "processing" && (
                    <div className="mt-4 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                      {translate("read_aloud_mockup_processing_body")}
                    </div>
                  )}

                  {speakState === "feedback" && (
                    <div className="mt-4 rounded-[18px] border border-slate-200 bg-[linear-gradient(180deg,#f7fbf8_0%,#fcfefd_100%)] px-4 py-4">
                      <div className="flex items-start gap-3">
                        <CheckCircleIcon className="mt-0.5 h-5 w-5 text-sky-700" />
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                            {translate("read_aloud_mockup_feedback_label")}
                          </p>
                          <p className="mt-2 text-sm text-slate-600">
                            {translate("read_aloud_mockup_heard_label")}
                          </p>
                          <p className="mt-1 text-base text-slate-950">
                            {activeSentence.heard}
                          </p>
                          <p className="mt-3 text-sm leading-relaxed text-slate-700">
                            {translate(activeSentence.hintKey)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {speakState === "ready" && (
                      <>
                        <ActionButton
                          icon={<SpeakerWaveIcon className="h-4 w-4" />}
                          label={translate("read_aloud_mockup_listen")}
                          tone="secondary"
                        />
                        <ActionButton
                          icon={<MicrophoneIcon className="h-4 w-4" />}
                          label={translate("read_aloud_mockup_record")}
                          onClick={startRecording}
                          tone="primary"
                        />
                      </>
                    )}

                    {speakState === "recording" && (
                      <ActionButton
                        icon={<PauseCircleIcon className="h-4 w-4" />}
                        label={translate("read_aloud_mockup_stop")}
                        onClick={stopRecording}
                        tone="primary"
                      />
                    )}

                    {speakState === "feedback" && (
                      <>
                        <ActionButton
                          icon={<ArrowPathIcon className="h-4 w-4" />}
                          label={translate("read_aloud_mockup_try_again")}
                          onClick={retrySentence}
                          tone="secondary"
                        />
                        <ActionButton
                          icon={<ChevronRightIcon className="h-4 w-4" />}
                          label={translate("read_aloud_mockup_next_sentence")}
                          onClick={moveToNextSentence}
                          tone="primary"
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              <article className="mt-5 rounded-[22px] border border-slate-200 bg-white px-5 py-5">
                <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                  {translate("read_aloud_mockup_text_label")}
                </p>
                <h3 className="mt-3 font-serif text-3xl text-slate-900">
                  Evening walk in the city
                </h3>
                <div className="mt-5 space-y-4 text-lg leading-9 text-slate-800">
                  {SENTENCES.map((sentence, index) => (
                    <p
                      key={sentence.id}
                      className={`rounded-[18px] px-3 py-2 transition ${
                        speakState !== "off" && index === activeSentenceIndex
                          ? "border border-sky-200 bg-sky-50 text-slate-950"
                          : "border border-transparent"
                      }`}
                    >
                      {sentence.text}
                    </p>
                  ))}
                </div>
              </article>
            </div>

            <div className="rounded-lg md:col-span-4 md:col-start-9">
              <div className="mb-4 rounded-[22px] border border-slate-200 bg-white px-4 py-4">
                <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                  {translate("read_aloud_mockup_helper_label")}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {translate("read_aloud_mockup_helper_body")}
                </p>
              </div>

              <h2 className="mb-4 bg-neutral-50 text-lg font-semibold md:sticky md:top-0 md:pt-5">
                {translate("vocabulary")}
              </h2>
              <ul className="m-0 w-full space-y-3 p-0 pb-6">
                <SidebarVocabularyCard
                  word="break"
                  translation="Pause"
                  detail={translate("read_aloud_mockup_vocab_break")}
                />
                <SidebarVocabularyCard
                  word="market"
                  translation="Markt"
                  detail={translate("read_aloud_mockup_vocab_market")}
                />
                <SidebarVocabularyCard
                  word="river"
                  translation="Fluss"
                  detail={translate("read_aloud_mockup_vocab_river")}
                />
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ActionButton(props: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  tone: "primary" | "secondary";
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-3 text-sm font-semibold ${
        props.tone === "primary"
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-300 bg-white text-slate-700"
      }`}
    >
      {props.icon}
      {props.label}
    </button>
  );
}

function SidebarVocabularyCard(props: {
  word: string;
  translation: string;
  detail: string;
}) {
  return (
    <li className="rounded-[20px] border border-slate-200 bg-white px-4 py-4">
      <p className="text-sm uppercase tracking-[0.14em] text-slate-500">
        {props.translation}
      </p>
      <p className="mt-1 text-xl font-semibold text-slate-900">{props.word}</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        {props.detail}
      </p>
    </li>
  );
}
